import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Drawer } from 'antd';
import { Link } from 'react-router-dom';

import { useSession } from '../../app/session-context.js';
import { api } from '../../shared/api-client.js';
import { resolvePageState, TableState } from '../../shared/page-state.js';
import { DRAWER_SIZE } from '../../shared/responsive-layout.js';
import { ExportNotificationList } from './export-notifications.js';
import { useExportNotificationFeed } from './use-export-notification-feed.js';

export function NotificationCenter() {
  const session = useSession();
  const canViewExports =
    session.capabilities.includes('consumption.export') ||
    session.capabilities.includes('commission.export');
  const [open, setOpen] = useState(false);
  const unread = useQuery({
    queryFn: api.notificationUnreadCount,
    queryKey: ['notifications', 'unread-count'],
    refetchInterval: 30_000,
  });
  const feed = useExportNotificationFeed({
    enabled: open,
    refetchInterval: open ? 5_000 : false,
  });
  const notificationState = resolvePageState({
    data: feed.query.data,
    error: feed.query.error,
    isEmpty: (data) => data.items.length === 0,
    pending: feed.query.isPending,
  });
  const unreadCount = unread.data?.unreadCount ?? 0;

  return (
    <>
      <Badge count={unreadCount} size="small">
        <Button
          aria-label={unreadCount > 0 ? `通知，${String(unreadCount)} 条未读` : '通知'}
          className="header-action"
          type="text"
          onClick={() => {
            setOpen(true);
          }}
        >
          通知
        </Button>
      </Badge>
      <Drawer
        open={open}
        size={DRAWER_SIZE.compact}
        title="通知中心"
        onClose={() => {
          setOpen(false);
        }}
      >
        <TableState
          emptyDescription="暂无通知"
          onRetry={() => void feed.query.refetch()}
          state={notificationState}
          {...(canViewExports
            ? {
                emptyAction: (
                  <Link
                    to="/exports"
                    onClick={() => {
                      setOpen(false);
                    }}
                  >
                    查看导出任务
                  </Link>
                ),
              }
            : {})}
        >
          <ExportNotificationList
            downloadPendingId={feed.download.variables?.exportTaskId}
            items={feed.query.data?.items ?? []}
            markReadPendingId={feed.markRead.variables?.id}
            retryPendingId={feed.retry.variables?.exportTaskId}
            onDownload={(item) => {
              feed.download.mutate(item);
            }}
            onMarkRead={(item) => {
              feed.markRead.mutate(item);
            }}
            onRetry={(item) => {
              feed.retry.mutate(item);
            }}
          />
        </TableState>
      </Drawer>
    </>
  );
}
